// contexts/AppContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { userLogin } from '../services/authApi';
import { getActiveCourse, getAjahn } from '../services/settings';

const AppContext = createContext();

// Initial state
const initialState = {
    // Pre-login data
    courseData: null,

    // Post-login data
    participantId: null,
    user_id: null,
    participation_type: null, // 'online', 'in-person', 'volunteer'
    retreat_language: '', // 'english', 'chinese', 'thai'
    user: null,
    inPersonAjahnsList: [],
    onlineAjahnsList: [],
    learningAjahnsList: [],
    interviewAjahnsList: [],
    formcheck: null,
    // UI state
    loading: true,
    error: null
};

// Action types
export const actionTypes = {
    SET_LOADING: 'SET_LOADING',
    SET_ERROR: 'SET_ERROR',
    SET_COURSE_DATA: 'SET_COURSE_DATA',
    SET_PARTICIPANT_DATA: 'SET_PARTICIPANT_DATA',
    SET_AJAHNS: 'SET_AJAHNS',
    UPDATE_USER_PROFILE: 'UPDATE_USER_PROFILE',
    SET_RETREAT_LANGUAGE: 'SET_RETREAT_LANGUAGE',
    LOGOUT: 'LOGOUT',
    UPDATE_FORMCHECK: 'UPDATE_FORMCHECK'
};

// Reducer function
function appReducer(state, action) {
    switch (action.type) {
        case actionTypes.SET_LOADING:
            return { ...state, loading: action.payload };

        case actionTypes.SET_ERROR:
            return { ...state, error: action.payload, loading: false };

        case actionTypes.SET_COURSE_DATA:
            return {
                ...state,
                courseData: action.payload,
                error: null
            };

        case actionTypes.UPDATE_FORMCHECK:
            return {
                ...state,
                formcheck: action.payload,
                loading: false,
                error: null
            };

        case actionTypes.SET_PARTICIPANT_DATA:
            return {
                ...state,
                participantId: action.payload.participant_id,
                participation_type: action.payload.participation_type,
                retreat_language: action.payload.retreat_language,
                user: action.payload.user,
                user_id: action.payload.user_id,
                loading: false,
                error: null
            };

        case actionTypes.SET_AJAHNS:
            return {
                ...state,
                learningAjahnsList: action.payload.filter(e => e.is_learning == 1),
                interviewAjahnsList: action.payload.filter(e => e.is_interview == 1),
                inPersonAjahnsList: action.payload.filter(e => e.mode === "In-person"),
                onlineAjahnsList: action.payload.filter(e => e.mode === "Online"),
                loading: false,
            };

        case actionTypes.UPDATE_USER_PROFILE:
            return {
                ...state,
                user: { ...state.user, ...action.payload }
            };

        case actionTypes.LOGOUT:
            return {
                ...initialState,
                loading: false
            };

        default:
            return state;
    }
}

// Provider component
export function AppProvider({ children }) {
    const [state, dispatch] = useReducer(appReducer, initialState);

    // comment out get from local storage
    // const [state, dispatch] = useReducer(appReducer, initialState, () => {
    //     try {
    //         const serializedState = localStorage.getItem('appState');
    //         const persistedState = serializedState === null ? undefined : JSON.parse(serializedState);
    //         return { ...initialState, ...persistedState };
    //     } catch (error) {
    //         console.warn('Failed to load persisted state:', error);
    //         return initialState;
    //     }
    // });

    const { t } = useTranslation();

    // Fetch course data (before login)
    const fetchCourseData = async () => {
        try {
            dispatch({ type: actionTypes.SET_LOADING, payload: true });
            const response = await getActiveCourse();
            const data = response.data.data;

            if (response.status === 200) {
                if (data.length === 0) {
                    dispatch({ type: actionTypes.SET_COURSE_DATA, payload: [] });
                    dispatch({ type: actionTypes.SET_LOADING, payload: false });
                } else {
                    dispatch({ type: actionTypes.SET_COURSE_DATA, payload: data[0] });
                }
            } else {
                dispatch({ type: actionTypes.SET_ERROR, payload: data.message });
            }
        } catch (error) {
            dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
        }
    };

    // Fetch ajahn list (after login)
    const fetchAjahn = async (courseId) => {
        try {
            const response = await getAjahn(courseId);
            if (response.length > 0) {
                dispatch({ type: actionTypes.SET_AJAHNS, payload: response });
            }
            return true;
        } catch (error) {
            console.error(`Error fetching ajahns`, error);
            dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
        }
    };

    // Login function
    const login = async (credentials) => {
        try {
            dispatch({ type: actionTypes.SET_LOADING, payload: true });

            const response = await userLogin(credentials);
            const data = response.data.data;
            if (data?.access_token) {
                toast.success(t("login.loginSuccess"));
                dispatch({
                    type: actionTypes.SET_PARTICIPANT_DATA,
                    payload: {
                        participant_id: data.user.id,
                        user_id: data.user.user_id,
                        participation_type: data.user.participation_type,
                        retreat_language: data.user.retreat_language,
                        user: data.user,
                    }
                });
                localStorage.setItem("token", JSON.stringify(data.access_token));
            } else {
                var message = "";
                if (data === 404) {
                    message = t("login.invalidPhone");
                } else if (data === 401) {
                    message = t("login.invalidCredentials");
                }
                dispatch({ type: actionTypes.SET_ERROR, payload: message });
            }
        } catch (error) {
            const message = t("general.ActionFailed");
            dispatch({ type: actionTypes.SET_ERROR, payload: message });
        }
    };

    // Logout function
    const logout = () => {
        dispatch({ type: actionTypes.LOGOUT });
        // Clear any stored tokens
        localStorage.clear();
        window.location.reload();
    };

    // Value provided to consumers
    const value = {
        ...state,
        login,
        logout,
        fetchCourseData,
        fetchAjahn,
        dispatch,
    };

    // Fetch course data on initial load
    useEffect(() => {
        fetchCourseData();
    }, []);

    useEffect(() => {
        if (state.courseData?.id) {
            fetchAjahn(state.courseData?.id);
        }
    }, [state.courseData?.id])

    // Persist state to localStorage when it changes
    useEffect(() => {
        const stateToPersist = {
            participantId: state?.participantId || null,
            participation_type: state?.participation_type || null,
            retreat_language: state?.retreat_language || null,
            user: state?.user || null,
            user_id: state?.user_id || null,
            courseData: state?.courseData || null,
            interviewAjahnsList: state?.interviewAjahnsList || null,
            learningAjahnsList: state?.learningAjahnsList || null,
            onlineAjahnsList: state?.onlineAjahnsList || null,
            inPersonAjahnsList: state?.inPersonAjahnsList || null,
            formcheck: state?.formcheck || null,
            loading: true
        }
        const serializedState = JSON.stringify(stateToPersist);
        localStorage.setItem('appState', serializedState);

    }, [state.user, state.courseData, state.interviewAjahnsList, state.formcheck]);

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
}

// Custom hook to use the context
export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within an AppProvider');
    }
    return context;
}

export const useAppDispatch = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppDispatch must be used within AppProvider');
    }
    return context.dispatch;
};